import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { api, authState } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import { openFile } from '../utils/openFile.js';
import { PdfFrame } from '../components/shared/PdfPages.jsx';

// ─── Secties ──────────────────────────────────────────────────────────────────

// Why geen voetbaltermen meer: deze lijst staat bij elke club, ook bij een tennisclub of
// een turnvereniging. "Voetbal" met Keepers en Scouting zei die niets. Een sectie die bij
// een club wél documenten bevat blijft hieronder gewoon staan (extraSections), dus wie
// stukken in een oude sectie heeft, raakt ze niet kwijt.
const SECTIONS = [
  { name: 'Dagelijks bestuur', icon: '🏛' },
  { name: 'Bestuur',           icon: '📋' },
  { name: 'Finance',           icon: '💰' },
  { name: 'Sport',             icon: '🏅' },
  { name: 'Facilitair',        icon: '🔧' },
];

const SECTION_CATEGORIES = {
  'Dagelijks bestuur': ['Algemeen bestuur', 'Vergaderingen', 'Besluitvorming'],
  'Bestuur':           ['Strategie', 'Beleid', 'Governance'],
  'Finance':           ['Begroting', 'Contributie', 'Sponsoring', 'Kosten'],
  'Sport':             ['Jeugd', 'Senioren', 'Technisch beleid', 'Wedstrijdzaken', 'Trainers en begeleiding'],
  'Facilitair':        ['Accommodatie', 'Materiaal', 'Vrijwilligers', 'Events'],
};

// De secties uit de lijst, plus de secties waar deze club documenten in heeft staan
function sectionsFor(countBySection) {
  const bekend = new Set(SECTIONS.map(s => s.name));
  const extra = Object.keys(countBySection)
    .filter(naam => naam && !bekend.has(naam))
    .sort()
    .map(naam => ({ name: naam, icon: '📁' }));
  return [...SECTIONS, ...extra];
}

// Bij bewerken moet de eigen sectie of categorie van een document kiesbaar blijven,
// ook als hij niet (meer) in de lijst staat
const metEigen = (lijst, eigen) => (eigen && !lijst.includes(eigen) ? [...lijst, eigen] : lijst);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPreviewable(fileType) {
  return fileType?.startsWith('image/') || fileType === 'application/pdf';
}

function fileIcon(fileType) {
  if (!fileType) return '📄';
  if (fileType.startsWith('image/')) return '🖼️';
  if (fileType === 'application/pdf') return '📕';
  if (fileType.includes('word') || fileType.includes('document')) return '📝';
  if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv')) return '📊';
  if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📙';
  return '📄';
}

function timeAgo(dateStr) {
  if (!dateStr) return '-';
  const days = Math.floor((Date.now() - parseServerDate(dateStr)) / 86400000);
  if (days === 0) return 'Vandaag';
  if (days === 1) return 'Gisteren';
  if (days < 7) return `${days} dagen geleden`;
  if (days < 30) return `${Math.floor(days / 7)} weken geleden`;
  if (days < 365) return `${Math.floor(days / 30)} maanden geleden`;
  return `${Math.floor(days / 365)} jaar geleden`;
}

function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="display:inline;vertical-align:middle;margin-right:2px">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function PolicySkeleton() {
  return (
    <div style="padding:8px 0">
      {[1, 2, 3].map(i => (
        <div key={i} style="display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid var(--color-border)">
          <div class="skeleton" style="width:36px;height:36px;border-radius:8px;flex-shrink:0" />
          <div style="flex:1">
            <div class="skeleton" style="height:13px;width:50%;border-radius:4px;margin-bottom:6px" />
            <div class="skeleton" style="height:11px;width:30%;border-radius:4px" />
          </div>
          <div class="skeleton" style="height:22px;width:70px;border-radius:6px" />
        </div>
      ))}
    </div>
  );
}

// ─── Section sidebar ──────────────────────────────────────────────────────────

function SectionSidebar({ documents, activeSection, onSelect, canUpload, onUpload }) {
  const totalCount = documents.length;

  // Count per section
  const countBySection = {};
  documents.forEach(d => {
    if (d.section) countBySection[d.section] = (countBySection[d.section] || 0) + 1;
  });

  return (
    <div style="width:220px;flex-shrink:0;border-right:1px solid var(--color-border);background:var(--color-surface);border-radius:12px 0 0 12px;overflow:hidden;display:flex;flex-direction:column">
      <div style="padding:16px 16px 8px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--color-text-muted)">
        Secties
      </div>

      {/* All */}
      <button
        onClick={() => onSelect(null)}
        style={`display:flex;align-items:center;gap:10px;padding:8px 16px;border:none;background:${activeSection === null ? 'var(--color-primary)' : 'transparent'};color:${activeSection === null ? '#fff' : 'var(--color-text)'};cursor:pointer;text-align:left;width:100%;font-size:13px;font-weight:500;transition:background .15s`}
      >
        <span style="font-size:16px">📂</span>
        <span style="flex:1">Alle beleid</span>
        <span style={`font-size:11px;padding:2px 6px;border-radius:10px;background:${activeSection === null ? 'rgba(255,255,255,.25)' : 'var(--color-border)'};color:${activeSection === null ? '#fff' : 'var(--color-text-muted)'}`}>
          {totalCount}
        </span>
      </button>

      {/* Sections */}
      <div style="flex:1;overflow-y:auto;padding-bottom:8px">
        {sectionsFor(countBySection).map(sec => (
          <button
            key={sec.name}
            onClick={() => onSelect(sec.name)}
            style={`display:flex;align-items:center;gap:10px;padding:8px 12px 8px 16px;border:none;background:${activeSection === sec.name ? 'var(--color-primary)' : 'transparent'};color:${activeSection === sec.name ? '#fff' : 'var(--color-text)'};cursor:pointer;text-align:left;width:100%;font-size:13px;font-weight:500;transition:background .15s`}
          >
            <span style="font-size:15px;flex-shrink:0">{sec.icon}</span>
            <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{sec.name}</span>
            <span style={`font-size:11px;padding:2px 5px;border-radius:10px;background:${activeSection === sec.name ? 'rgba(255,255,255,.25)' : 'var(--color-border)'};color:${activeSection === sec.name ? '#fff' : 'var(--color-text-muted)'};flex-shrink:0`}>
              {countBySection[sec.name] || 0}
            </span>
          </button>
        ))}
      </div>

      {canUpload && (
        <div style="padding:8px 12px;border-top:1px solid var(--color-border)">
          <button
            onClick={onUpload}
            style="display:flex;align-items:center;gap:8px;padding:7px 10px;border:1px dashed var(--color-border);border-radius:8px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:12px;font-weight:500;width:100%;transition:all .15s"
          >
            <span>+</span> Uploaden
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Policy document row ──────────────────────────────────────────────────────

function PolicyRow({ doc, currentUserId, canDelete, canEdit, onPreview, onDelete, onVersions, onAccess, onEdit }) {
  const isUploader = Number(doc.uploaded_by) === currentUserId;

  return (
    <div style="display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--color-border);transition:background .15s">
      {/* File icon */}
      <div style="width:36px;height:36px;border-radius:8px;background:var(--color-bg);display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0;border:1px solid var(--color-border)">
        {fileIcon(doc.file_type)}
      </div>

      {/* Name + meta */}
      <div style="flex:2;min-width:0">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
          <span style="font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:280px" title={doc.file_name}>
            {doc.file_name}
          </span>
          {(doc.version || 1) > 1 && (
            <span style="font-size:10px;background:var(--color-primary);color:#fff;padding:1px 5px;border-radius:10px;flex-shrink:0">
              v{doc.version}
            </span>
          )}
          {doc.restricted_role_count > 0 && (
            <span style="font-size:10px;color:var(--color-warning,#d97706);display:flex;align-items:center;flex-shrink:0">
              <LockIcon />Beperkt
            </span>
          )}
        </div>
        {doc.description && (
          <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{doc.description}</div>
        )}
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:1px">
          {formatFileSize(doc.file_size)} · {doc.uploaded_by_name || '?'} · {timeAgo(doc.created_at)}
        </div>
      </div>

      {/* Section / category badges */}
      <div style="display:flex;flex-direction:column;gap:3px;flex-shrink:0;min-width:80px">
        {doc.section && (
          <span style="font-size:10px;background:var(--color-primary);color:#fff;padding:1px 7px;border-radius:10px;white-space:nowrap;text-align:center">{doc.section}</span>
        )}
        {(doc.policy_category || (doc.category && doc.category !== 'beleid')) && (
          <span style="font-size:10px;background:var(--color-border);color:var(--color-text-muted);padding:1px 7px;border-radius:10px;white-space:nowrap;text-align:center">{doc.policy_category || doc.category}</span>
        )}
      </div>

      {/* Actions */}
      <div style="display:flex;gap:4px;flex-shrink:0;flex-wrap:wrap;justify-content:flex-end">
        {isPreviewable(doc.file_type) && (
          <button onClick={() => onPreview(doc)} title="Bekijk"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
            👁 Bekijk
          </button>
        )}
        <button onClick={() => openFile(`/api/documents/${doc.id}/download`, doc.file_name)} title="Download"
          style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
          ⬇
        </button>
        <button onClick={() => onVersions(doc)} title="Versies"
          style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
          🕐
        </button>
        {(canEdit || isUploader) && (
          <button onClick={() => onEdit(doc)} title="Bewerken"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
            ✏️
          </button>
        )}
        {(canEdit || isUploader) && (
          <button onClick={() => onAccess(doc)} title="Toegang"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
            🔐
          </button>
        )}
        {(canDelete || isUploader) && (
          <button onClick={() => onDelete(doc)} title="Verwijder"
            style="padding:5px 9px;font-size:11px;border:1px solid var(--color-border);border-radius:6px;background:transparent;color:var(--color-danger,#dc2626);border-color:var(--color-danger,#dc2626);cursor:pointer">
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Upload modal ─────────────────────────────────────────────────────────────

function UploadModal({ defaultSection, onClose, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [section, setSection] = useState(defaultSection || '');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const availableCategories = section ? (SECTION_CATEGORIES[section] || []) : [];

  const pickFile = (f) => { setFile(f); if (!displayName) setDisplayName(f.name); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) pickFile(f); };

  const handleUpload = async () => {
    if (!file) { setError('Selecteer een bestand'); return; }
    setUploading(true); setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (displayName.trim()) form.append('displayName', displayName.trim());
      if (description.trim()) form.append('description', description.trim());
      // category markeert het document als beleid; het label heeft een eigen veld,
      // anders valt het document buiten de bibliotheek (migratie 158)
      form.append('category', 'beleid');
      if (category) form.append('policy_category', category);
      if (section) form.append('section', section);

      const res = await fetch('/api/documents', { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(data.error); e.code = data.error; throw e; }
      onUploaded();
      onClose();
    } catch (e) {
      setError(translateError(e.code) || 'Upload mislukt');
    } finally { setUploading(false); }
  };

  return (
    <Modal isOpen title="Beleidsdocument uploaden" onClose={onClose}>
      {error && <div class="alert alert-error mb-4">{error}</div>}

      {/* Drop zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        style={`margin-bottom:16px;border:2px dashed ${dragging ? 'var(--color-primary)' : file ? 'var(--color-primary)' : 'var(--color-border)'};border-radius:10px;padding:24px;text-align:center;cursor:pointer;transition:all .15s;background:${dragging ? 'var(--color-primary)18' : file ? 'var(--color-primary)08' : 'transparent'}`}
      >
        <div style="font-size:28px;margin-bottom:8px">{dragging ? '🎯' : file ? fileIcon(file.type) : '📤'}</div>
        <div style="font-size:13px;font-weight:600">
          {dragging ? 'Laat los om te uploaden' : file ? file.name : 'Sleep een bestand hierheen'}
        </div>
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">
          {file ? formatFileSize(file.size) : 'of klik om te bladeren · PDF, Word, Excel — max 10 MB'}
        </div>
        <input ref={fileRef} type="file" style="display:none" accept=".pdf,.doc,.docx,.xlsx,.xls,.ppt,.pptx" onChange={e => pickFile(e.target.files[0])} />
      </div>

      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Weergavenaam</label>
        <input class="form-input" value={displayName} onInput={e => setDisplayName(e.target.value)} placeholder="Laat leeg om bestandsnaam te gebruiken" />
      </div>

      <div style="margin-bottom:12px">
        <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Omschrijving</label>
        <textarea class="form-input" value={description} onInput={e => setDescription(e.target.value)}
          placeholder="Korte omschrijving (optioneel)" rows={2} style="resize:vertical;min-height:56px" />
        <AiAssist kind="policy_description" value={description} context={{ title: displayName || file?.name || '' }} onUse={setDescription} />
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">
        <div>
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Sectie</label>
          <select class="form-select" value={section} onChange={e => { setSection(e.target.value); setCategory(''); }}>
            <option value="">— Kies sectie</option>
            {SECTIONS.map(s => <option key={s.name} value={s.name}>{s.icon} {s.name}</option>)}
          </select>
        </div>
        <div>
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:5px">Categorie</label>
          <select class="form-select" value={category} onChange={e => setCategory(e.target.value)} disabled={!section}>
            <option value="">— Kies categorie</option>
            {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div class="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose} disabled={uploading}>Annuleer</Button>
        <Button variant="primary" onClick={handleUpload} disabled={uploading || !file}>
          {uploading ? 'Uploaden...' : 'Uploaden'}
        </Button>
      </div>
    </Modal>
  );
}

// ─── Edit modal ───────────────────────────────────────────────────────────────

function EditModal({ doc, onClose, onSaved }) {
  const [form, setForm] = useState({ file_name: '', section: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (doc) setForm({
      file_name: doc.file_name || '',
      section: doc.section || '',
      category: doc.policy_category || (doc.category === 'beleid' ? '' : (doc.category || '')),
    });
    setError(null);
  }, [doc]);

  const availableCategories = metEigen(form.section ? (SECTION_CATEGORIES[form.section] || []) : [], form.category);

  const handleSubmit = async () => {
    setSaving(true); setError(null);
    try {
      await api(`/api/documents/${doc.id}`, {
        method: 'PUT',
        body: JSON.stringify({ file_name: form.file_name || undefined, section: form.section || null, category: 'beleid', policy_category: form.category || null }),
      });
      onSaved(); onClose();
    } catch (err) { setError(translateError(err.code)); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen title="Document bewerken" onClose={onClose}>
      {error && <div class="alert alert-error mb-3">{error}</div>}
      <div class="form-group">
        <label class="form-label">Naam</label>
        <input class="form-input" value={form.file_name} onInput={e => setForm(p => ({ ...p, file_name: e.target.value }))} />
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
        <div class="form-group">
          <label class="form-label">Sectie</label>
          <select class="form-select" value={form.section} onChange={e => setForm(p => ({ ...p, section: e.target.value, category: '' }))}>
            <option value="">— Geen sectie</option>
            {metEigen(SECTIONS.map(x => x.name), form.section).map(naam => <option key={naam} value={naam}>{naam}</option>)}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Categorie</label>
          <select class="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} disabled={!form.section}>
            <option value="">— Geen categorie</option>
            {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div class="flex gap-2 justify-end mt-4">
        <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>{saving ? t('common.loading') : t('common.save')}</Button>
      </div>
    </Modal>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function PolicyLibraryView() {
  const { hasPermission } = useAuth();
  const currentUserId = Number(authState.value.userId);

  const canUpload = hasPermission('documents', 'create');
  const canEdit   = hasPermission('documents', 'update');
  const canDelete = hasPermission('documents', 'delete');

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [search, setSearch] = useState('');

  // Modals
  const [showUpload, setShowUpload] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [versionDoc, setVersionDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [versionUploadError, setVersionUploadError] = useState(null);
  const [accessDoc, setAccessDoc] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(false);

  const versionUploadRef = useRef(null);

  const fetchDocuments = () => {
    setLoading(true);
    const params = new URLSearchParams({ category: 'beleid' });
    if (activeSection) params.set('section', activeSection);
    if (search.trim()) params.set('search', search.trim());
    api(`/api/documents?${params}`)
      .then(setDocuments)
      .catch(err => setError(translateError(err.code, err.detail)))
      .finally(() => setLoading(false));
  };

  // Fetch all docs (unfiltered) for sidebar counts
  const [allDocuments, setAllDocuments] = useState([]);
  const fetchAll = () => api('/api/documents?category=beleid').then(setAllDocuments).catch(() => {});

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchDocuments(); }, [activeSection, search]);

  const handleDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await api(`/api/documents/${deleteDoc.id}`, { method: 'DELETE' });
      setDeleteDoc(null);
      fetchDocuments();
      fetchAll();
    } catch (err) { setError(translateError(err.code, err.detail)); setDeleteDoc(null); }
    finally { setDeleting(false); }
  };

  const openVersionHistory = async (doc) => {
    setVersionDoc(doc); setVersions([]); setLoadingVersions(true); setVersionUploadError(null);
    try { setVersions(await api(`/api/documents/${doc.id}/versions`)); }
    catch { setVersions([]); }
    finally { setLoadingVersions(false); }
  };

  const handleVersionFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !versionDoc) return;
    setUploadingVersion(true); setVersionUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/documents/${versionDoc.id}/versions`, { method: 'POST', body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(data.error); e.code = data.error; throw e; }
      setVersions(await api(`/api/documents/${versionDoc.id}/versions`));
      fetchDocuments(); fetchAll();
    } catch (e) { setVersionUploadError(translateError(e.code, e.detail)); }
    finally { setUploadingVersion(false); if (versionUploadRef.current) versionUploadRef.current.value = ''; }
  };

  const openAccessModal = async (doc) => {
    setAccessDoc(doc); setSelectedRoleIds([]); setLoadingAccess(true);
    try {
      const [accessData, rolesData] = await Promise.all([
        api(`/api/documents/${doc.id}/access`),
        availableRoles.length ? Promise.resolve(availableRoles) : api('/api/users/roles/list'),
      ]);
      if (!availableRoles.length) setAvailableRoles(rolesData);
      setSelectedRoleIds(accessData.roleIds || []);
    } catch { setSelectedRoleIds([]); }
    finally { setLoadingAccess(false); }
  };

  const handleSaveAccess = async () => {
    if (!accessDoc) return;
    setSavingAccess(true);
    try {
      await api(`/api/documents/${accessDoc.id}/access`, { method: 'PUT', body: JSON.stringify({ roleIds: selectedRoleIds }) });
      fetchDocuments(); setAccessDoc(null);
    } catch (err) { setError(translateError(err.code, err.detail)); }
    finally { setSavingAccess(false); }
  };

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('policy.title')}</h1>
        {canUpload && (
          <Button variant="primary" size="sm" onClick={() => setShowUpload(true)}>
            + Document uploaden
          </Button>
        )}
      </div>

      {error && <div class="alert alert-error mb-4">{error}</div>}

      {/* Main layout */}
      <div style="display:flex;gap:0;border:1px solid var(--color-border);border-radius:12px;overflow:hidden;background:var(--color-surface);min-height:500px">
        <SectionSidebar
          documents={allDocuments}
          activeSection={activeSection}
          onSelect={s => { setActiveSection(s); setSearch(''); }}
          canUpload={canUpload}
          onUpload={() => setShowUpload(true)}
        />

        {/* Right pane */}
        <div style="flex:1;min-width:0;display:flex;flex-direction:column">
          {/* Toolbar */}
          <div style="display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--color-border);background:var(--color-bg)">
            <div style="position:relative;flex:1;max-width:320px">
              <span style="position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--color-text-muted);font-size:14px;pointer-events:none">🔍</span>
              <input
                class="form-input"
                value={search}
                onInput={e => setSearch(e.target.value)}
                placeholder="Zoek op naam..."
                style="padding-left:32px;height:34px;font-size:13px"
              />
            </div>
            <span style="font-size:12px;color:var(--color-text-muted);white-space:nowrap">
              {documents.length} document{documents.length !== 1 ? 'en' : ''}
              {activeSection ? ` in "${activeSection}"` : ''}
            </span>
          </div>

          {/* Document list */}
          <div style="flex:1;overflow-y:auto">
            {loading ? (
              <PolicySkeleton />
            ) : documents.length === 0 ? (
              <div style="padding:48px 32px;text-align:center">
                <div style="font-size:40px;margin-bottom:12px">📭</div>
                <div style="font-size:14px;color:var(--color-text-muted)">
                  {search ? 'Geen documenten gevonden' : activeSection ? `Geen documenten in "${activeSection}"` : 'Nog geen beleidsdocumenten'}
                </div>
                {canUpload && !search && (
                  <button
                    onClick={() => setShowUpload(true)}
                    style="margin-top:16px;padding:8px 18px;border:1px solid var(--color-primary);border-radius:8px;background:transparent;color:var(--color-primary);cursor:pointer;font-size:13px;font-weight:500"
                  >
                    + Eerste document uploaden
                  </button>
                )}
              </div>
            ) : (
              documents.map(doc => (
                <PolicyRow
                  key={doc.id}
                  doc={doc}
                  currentUserId={currentUserId}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onPreview={setPreviewDoc}
                  onDelete={setDeleteDoc}
                  onVersions={openVersionHistory}
                  onAccess={openAccessModal}
                  onEdit={setEditDoc}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}

      {showUpload && (
        <UploadModal
          defaultSection={activeSection}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { fetchDocuments(); fetchAll(); }}
        />
      )}

      {editDoc && (
        <EditModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={() => { fetchDocuments(); fetchAll(); }}
        />
      )}

      <Modal isOpen={!!deleteDoc} title="Document verwijderen" onClose={() => !deleting && setDeleteDoc(null)}>
        <p style="margin-bottom:24px;color:var(--color-text-muted);line-height:1.6">
          Weet je zeker dat je <strong>{deleteDoc?.file_name}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setDeleteDoc(null)} disabled={deleting}>Annuleer</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Verwijderen...' : 'Verwijder'}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={!!previewDoc} title={previewDoc?.file_name || 'Voorbeeld'} onClose={() => setPreviewDoc(null)}>
        <div style="max-height:70vh;overflow:auto">
          {previewDoc?.file_type?.startsWith('image/') ? (
            <img src={`/api/documents/${previewDoc.id}/preview`} alt={previewDoc.file_name} style="max-width:100%;height:auto;display:block" />
          ) : previewDoc?.file_type === 'application/pdf' ? (
            <PdfFrame src={`/api/documents/${previewDoc.id}/preview`} style="width:100%;height:65vh;border:none" title={previewDoc.file_name} />
          ) : (
            <p style="padding:32px;text-align:center;color:var(--color-text-muted)">Voorbeeld niet beschikbaar.</p>
          )}
        </div>
        <div class="flex justify-end mt-4">
          <Button variant="secondary" onClick={() => setPreviewDoc(null)}>Sluiten</Button>
        </div>
      </Modal>

      <Modal isOpen={!!versionDoc} title="Versiegeschiedenis" onClose={() => setVersionDoc(null)}>
        {loadingVersions ? (
          <div style="padding:24px;text-align:center;color:var(--color-text-muted)">Laden...</div>
        ) : (
          <div style="margin-bottom:16px">
            {versions.map((v, i) => (
              <div key={v.id} style="display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--color-border)">
                <div style="flex:1">
                  <div style="font-size:13px;font-weight:500;display:flex;align-items:center;gap:6px">
                    Versie {v.version || (versions.length - i)}
                    {i === 0 && <span style="font-size:10px;background:var(--color-primary);color:#fff;padding:1px 6px;border-radius:10px">Huidig</span>}
                  </div>
                  <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px">
                    {v.file_name} · {formatFileSize(v.file_size)} · {v.uploaded_by_name || '-'} · {timeAgo(v.created_at)}
                  </div>
                </div>
                <button onClick={() => openFile(`/api/documents/${v.id}/download`, v.file_name)}
                  style="padding:5px 10px;font-size:12px;border:1px solid var(--color-border);border-radius:6px;background:var(--color-bg);color:var(--color-text);cursor:pointer">
                  ⬇ Download
                </button>
              </div>
            ))}
          </div>
        )}
        {versionUploadError && <div class="alert alert-error mt-2">{versionUploadError}</div>}
        <div class="flex gap-2 justify-end">
          {canUpload && (
            <>
              <Button variant="primary" size="sm" onClick={() => versionUploadRef.current?.click()} disabled={uploadingVersion}>
                {uploadingVersion ? 'Uploaden...' : '+ Nieuwe versie'}
              </Button>
              <input ref={versionUploadRef} type="file" style="display:none" onChange={handleVersionFileSelect} />
            </>
          )}
          <Button variant="secondary" onClick={() => setVersionDoc(null)}>Sluiten</Button>
        </div>
      </Modal>

      <Modal isOpen={!!accessDoc} title="Toegangsbeheer" onClose={() => !savingAccess && setAccessDoc(null)}>
        <p style="margin-bottom:16px;color:var(--color-text-muted);font-size:13px;line-height:1.5">
          Kies welke rollen dit document mogen zien. Laat leeg voor toegang voor iedereen.
        </p>
        {loadingAccess ? (
          <div style="padding:24px;text-align:center;color:var(--color-text-muted)">Laden...</div>
        ) : (
          <div style="margin-bottom:20px;border:1px solid var(--color-border);border-radius:8px;overflow:hidden;max-height:240px;overflow-y:auto">
            {availableRoles.filter(r => !['Functioneel Beheerder', 'Documentbeheerder'].includes(r.name)).map(role => (
              <label key={role.id} style="display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid var(--color-border);cursor:pointer;font-size:13px">
                <input type="checkbox" checked={selectedRoleIds.includes(role.id)}
                  onChange={() => setSelectedRoleIds(prev => prev.includes(role.id) ? prev.filter(r => r !== role.id) : [...prev, role.id])}
                  style="width:15px;height:15px;cursor:pointer" />
                {role.name}
              </label>
            ))}
          </div>
        )}
        <p style="font-size:12px;color:var(--color-text-muted);margin-bottom:16px">
          {selectedRoleIds.length === 0 ? '🌐 Zichtbaar voor iedereen' : `🔒 Beperkt tot ${selectedRoleIds.length} rol${selectedRoleIds.length !== 1 ? 'len' : ''}`}
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setAccessDoc(null)} disabled={savingAccess}>Annuleer</Button>
          <Button variant="primary" onClick={handleSaveAccess} disabled={savingAccess || loadingAccess}>
            {savingAccess ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
